// @flow

import React, {useEffect, useRef, useState} from "react";
import Button from "./Button";
import "./SearchBar.scss";
import {scrollToTop} from "../../helper/anchor";
import {useTranslation} from "react-i18next";
import type {PreviewCategory, PreviewVehicle, SearchPreview} from "../../helper/searchPreview";
import {isEmptyPreview, splitByMatches} from "../../helper/searchPreview";
import type {Product} from "../../models/Product";
import {determineDistributionChannelForPriceDisplay, isOnSale} from "../../models/Product";

type SearchBarProps = {
    searchProducts: string => void,
    handleChange: string => void,
    fetchPreview: string => void,
    clearPreview: () => void,
    openProduct: Product => void,
    openCategory: PreviewCategory => void,
    selectVehicle: PreviewVehicle => void,
    formatPrice: any => string,
    value: string,
    preview: ?SearchPreview,
    showPrice: boolean,
    searchPlaceholder: string,
}

// Two characters is where the search itself begins to narrow anything down - the infix field is
// built from two upwards.
const MINIMUM_TYPED_LENGTH = 2;

// Long enough that typing a word costs one request rather than one per letter, short enough that the
// preview stands there by the time the customer stops to look at it.
const DEBOUNCE_MILLIS = 200;

const NOTHING_HIGHLIGHTED = -1;

const LISTBOX_ID = "hpm-searchPreview";

const optionId = (index: number) => `hpm-searchPreview-option-${index}`;

type Entry =
    | {kind: "vehicle", vehicle: PreviewVehicle}
    | {kind: "category", category: PreviewCategory}
    | {kind: "product", product: Product}
    | {kind: "all"};

// the order the groups are shown in is the order the arrow keys walk them
const entriesOf = (preview: ?SearchPreview): Entry[] => isEmptyPreview(preview) || !preview
    ? []
    : [
        ...preview.vehicles.map(vehicle => ({kind: "vehicle", vehicle})),
        ...preview.categories.map(category => ({kind: "category", category})),
        ...preview.products.map(product => ({kind: "product", product})),
        {kind: "all"},
    ];

const Highlighted = ({text, typed}: {text: string, typed: string}) =>
    splitByMatches(text, typed).map((part, index) => part.isMatch
        ? <mark key={index}>{part.text}</mark>
        : <React.Fragment key={index}>{part.text}</React.Fragment>);

const VehicleIcon = () =>
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11m-14 0h14m-14 0a2 2 0 0 0-2 2v3h2m14-5a2 2 0 0 1 2 2v3h-2M5 16v2m14-2v2M7.5 14h.01M16.5 14h.01"
              fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>;

const CategoryIcon = () =>
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z"
              fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
    </svg>;

// the price the product tile shows: on sale it is the one of the channel the sale runs in
const productPriceOf = (product: Product) => isOnSale(product)
    ? (determineDistributionChannelForPriceDisplay(product) || {}).price
    : product.price;

// the picture of a vehicle or category where there is one, the icon of its group where there is none
const EntryPicture = ({imageUrl, icon, fit}: {imageUrl: ?string, icon: any, fit: "cover" | "contain"}) => imageUrl
    ? <span className={`hpm-searchPreview__thumbnail hpm-searchPreview__thumbnail--${fit}`}>
        <img src={imageUrl} alt="" loading="lazy"/>
    </span>
    : <span className="hpm-searchPreview__icon">{icon}</span>;

const productImageOf = (product: Product): ?string => {
    const galleryImage = product.galleryImage || {};

    return (galleryImage.previewImage || {}).attachmentUrl || galleryImage.attachmentUrl;
};

/**
 * The search bar and the preview it opens while a customer types: the vehicles and categories the
 * typed words lead to, the first products, and the way to all results. The preview comes from the
 * very search the result page runs, so what it shows is what the customer gets.
 *
 * <p>Only typing asks for a preview. Picking an entry and submitting a search both change the input
 * as well, and neither may bring the preview back up over the page the customer just asked for; that
 * is what the typed ref keeps apart, and why it is a ref and not state - it steers the next effect
 * without being a reason to render.
 */
const SearchBar = (props: SearchBarProps) => {
    const {t} = useTranslation();
    const [isOpen, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(NOTHING_HIGHLIGHTED);
    const typed = useRef(null);
    const scheduledRequest = useRef(null);

    const entries = isOpen ? entriesOf(props.preview) : [];

    useEffect(() => {
        if (typed.current === null) {
            return () => {};
        }

        const value = typed.current.trim();

        if (value.length < MINIMUM_TYPED_LENGTH) {
            props.clearPreview();

            return () => {};
        }

        scheduledRequest.current = setTimeout(() => props.fetchPreview(value), DEBOUNCE_MILLIS);

        // Every keystroke drops the request the one before it had scheduled. What arrives late from a
        // request already sent is dropped in getSearchPreview, where the preview for "an" must not
        // land on top of the preview for "anh".
        return () => clearTimeout(scheduledRequest.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value]);

    const closePreview = () => {
        // a search submitted within the debounce must not be followed by the preview it had scheduled
        clearTimeout(scheduledRequest.current);
        typed.current = null;
        setOpen(false);
        setHighlighted(NOTHING_HIGHLIGHTED);
        props.clearPreview();
    };

    const searchFor = (phrase: string) => {
        closePreview();

        if (phrase) {
            props.searchProducts(phrase);
        }

        scrollToTop();
    };

    const choose = (entry: Entry) => {
        if (entry.kind === "all") {
            searchFor(props.value);

            return;
        }

        closePreview();

        if (entry.kind === "vehicle") {
            props.selectVehicle(entry.vehicle);
        } else if (entry.kind === "category") {
            props.openCategory(entry.category);
        } else {
            props.openProduct(entry.product);
        }

        scrollToTop();
    };

    const onChange = (value: string) => {
        typed.current = value;
        setOpen(true);
        setHighlighted(NOTHING_HIGHLIGHTED);
        props.handleChange(value);
    };

    const onSubmit = event => {
        event.preventDefault();

        if (entries[highlighted]) {
            choose(entries[highlighted]);

            return;
        }

        searchFor(props.value);
    };

    const moveHighlight = (by: number) => {
        const next = highlighted + by;

        setHighlighted(next < 0 || next >= entries.length ? NOTHING_HIGHLIGHTED : next);
    };

    const onKeyDown = event => {
        if (event.key === "Escape") {
            closePreview();

            return;
        }

        if (entries.length === 0) {
            return;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            // or the caret would jump to the far end of what has been typed
            event.preventDefault();
            moveHighlight(event.key === "ArrowDown" ? 1 : -1);
        }
    };

    // onMouseDown and not onClick: the click would come after the input has lost focus, and the
    // preview would be gone before it arrives.
    const optionProps = (index: number, modifier: string) => ({
        id: optionId(index),
        role: "option",
        "aria-selected": index === highlighted,
        className: `hpm-searchPreview__option hpm-searchPreview__option--${modifier}`
            + (index === highlighted ? " hpm-searchPreview__option--highlighted" : ""),
        onMouseDown: event => {
            event.preventDefault();
            choose(entries[index]);
        },
        onMouseEnter: () => setHighlighted(index),
    });

    const typedText = props.value || "";

    const renderGroup = (kind: string, heading: string, render: (entry: Entry, index: number) => any) => {
        const groupEntries = entries
            .map((entry, index) => ({entry, index}))
            .filter(({entry}) => entry.kind === kind);

        if (groupEntries.length === 0) {
            return null;
        }

        const headingId = `${LISTBOX_ID}-${kind}`;

        return <div role="group" aria-labelledby={headingId} className="hpm-searchPreview__group">
            <div id={headingId} role="presentation" className="hpm-searchPreview__heading">{heading}</div>
            {groupEntries.map(({entry, index}) => render(entry, index))}
        </div>;
    };

    const totalItems = props.preview ? props.preview.totalItems : 0;

    return <form onSubmit={onSubmit} className="hpm-searchBar" role="search">
        <input type="text" placeholder={props.searchPlaceholder} value={props.value}
               role="combobox" aria-expanded={entries.length > 0} aria-autocomplete="list"
               aria-controls={LISTBOX_ID}
               aria-activedescendant={entries[highlighted] ? optionId(highlighted) : undefined}
               autoComplete="off"
               onChange={event => onChange(event.target.value)}
               onKeyDown={onKeyDown}
               onBlur={() => isOpen && closePreview()}/>
        <Button aria-label={t("navigation.searchBarSubmit")} data-tooltip={t("navigation.searchBarSubmit")}
                name="search" type={"submit"}/>
        {entries.length > 0 &&
            <div className="hpm-searchPreview" id={LISTBOX_ID} role="listbox"
                 aria-label={t("navigation.searchSuggestions")}>
                {renderGroup("vehicle", t("navigation.searchPreviewVehicles"), (entry, index) =>
                    entry.kind === "vehicle" &&
                    <div key={entry.vehicle.key} {...optionProps(index, "vehicle")}>
                        <EntryPicture imageUrl={entry.vehicle.imageUrl} icon={<VehicleIcon/>} fit="contain"/>
                        <span className="hpm-searchPreview__text">
                            <span className="hpm-searchPreview__title">
                                <Highlighted text={entry.vehicle.title} typed={typedText}/>
                            </span>
                            {entry.vehicle.count != null &&
                                <span className="hpm-searchPreview__meta">
                                    {t("navigation.searchPreviewProductCount", {count: entry.vehicle.count})}
                                </span>}
                        </span>
                    </div>)}
                {renderGroup("category", t("navigation.searchPreviewCategories"), (entry, index) =>
                    entry.kind === "category" &&
                    <div key={entry.category.key} {...optionProps(index, "category")}>
                        <EntryPicture imageUrl={entry.category.imageUrl} icon={<CategoryIcon/>} fit="cover"/>
                        <span className="hpm-searchPreview__text">
                            <span className="hpm-searchPreview__title">
                                <Highlighted text={entry.category.title} typed={typedText}/>
                            </span>
                            {entry.category.count != null &&
                                <span className="hpm-searchPreview__meta">
                                    {t("navigation.searchPreviewProductCount", {count: entry.category.count})}
                                </span>}
                        </span>
                    </div>)}
                {renderGroup("product", t("navigation.searchPreviewProducts"), (entry, index) => {
                    if (entry.kind !== "product") return null;

                    const {product} = entry;
                    const image = productImageOf(product);
                    const price = productPriceOf(product);

                    return <div key={product.id} {...optionProps(index, "product")}>
                        <span className="hpm-searchPreview__thumbnail">
                            {image && <img src={image} alt="" loading="lazy"/>}
                        </span>
                        <span className="hpm-searchPreview__text">
                            <span className="hpm-searchPreview__title">
                                <Highlighted text={product.name} typed={typedText}/>
                            </span>
                            {props.showPrice && price &&
                                <span className="hpm-searchPreview__meta">{props.formatPrice(price)}</span>}
                        </span>
                    </div>;
                })}
                <div {...optionProps(entries.length - 1, "all")}>
                    {t("navigation.searchPreviewShowAll", {count: totalItems})}
                </div>
            </div>}
    </form>;
};

export default SearchBar;
